import React, { useEffect, useMemo } from 'react';
import { useFetchData } from 'services/api/useFetchData';
import { getAvailableIntegration } from './availableIntegrations';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import PageAlert from 'components/alerts/PageAlert';
import { PageAlertType } from 'types/AlertTypes';
import IntegrationNotificationsList from './IntegrationNotificationsList';
import ModulePriceBox from 'components/billing/ModulePriceBox';
import { useOrderableModuleFeatures } from '../billing/functions/useOrderableModuleFeatures';

export interface IntegrationPageContentProps {
    response: any, 
    handleRefetch: () => void;
}

export interface IntegrationPageActionsProps {
    response: any;
    form: React.ComponentType<{ viewKey?: string }>
    integrationSlug: string;
    integrationId?: number;
    featureKey?: string;
    handleRefetch?: () => void;
}

interface IntegrationPageProps {
	slug: string;
	id?: number;
};

const IntegrationPage: React.FC<IntegrationPageProps> = ({ slug, id }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const history = useHistory();
    const { response, refetch } = useFetchData({apiUrl: id ? `get_integration/${slug}/${id}` : ''});
    const integrationData = useMemo(() => getAvailableIntegration(slug), [slug]);
    const moduleFeatures = useOrderableModuleFeatures();
    const queryParams = new URLSearchParams(location.search);
    const status = queryParams.get('status');
    const action = queryParams.get('action');

    // Get the specific integration page content and actions
    const IntegrationPageContent = integrationData?.integrationsPage.content;
    const IntegrationPageActions = integrationData?.integrationsPage.actions;
    const IntegrationPageForm = integrationData?.integrationsPage.form;

    // Remove alert at refresh
    useEffect(() => {
        if (status && action) {
            const newParams = new URLSearchParams(location.search);
            newParams.delete("status");
            newParams.delete("action");

            history.replace(`${location.pathname}?${newParams.toString()}`);
        }
    }, [status, action]);

    // Return if integration data is null
    if (!integrationData) return null;

    // Determine to show an alert on top of the page
    const showAlert: PageAlertType | null = status && action ? {
        type: status === 'success' ? 'success' : 'danger',
        message: `integrations.alert.${action}`,
        messageOptions: { name: integrationData.name },
    } : null;

    // Determine the status on either the is active state or just the status
    const effectiveStatus = response?.is_active === false ? 'deactivated' : response?.status;

    // Get the price of the integration feature while it is not active yet
    const featureKey = integrationData.featureKey;
    const isFeatureActive = featureKey ? moduleFeatures?.availableFeatures.includes(featureKey) : true;
    const orderableModule = featureKey && !isFeatureActive
        ? moduleFeatures?.orderableModules.find(module => module.feature === featureKey)
        : undefined;

    return (
        <>
            {showAlert && (
                <PageAlert
                    type={showAlert.type}
                    message={showAlert.message}
                    messageOptions={showAlert.messageOptions}
                />
            )}
            <div className='integration-card'>
                <div className='integration-card-header'>
                    <div className='left-items'>
                        <img src={integrationData.logo} alt={integrationData.name} />
                    </div>
                    {(effectiveStatus || orderableModule) && (
                        <div className='right-items'>
                            {effectiveStatus && (
                                <div className={`integration-status-badge ${effectiveStatus}`}>
                                    {t(`integrations.status.${effectiveStatus}`)}
                                </div>
                            )}
                            {orderableModule && (
                                <ModulePriceBox
                                    priceType={orderableModule.price_type}
                                    flatPrice={orderableModule.flat_price}
                                    pricePerSeat={orderableModule.price_per_seat}
                                />
                            )}
                        </div>
                    )}
                </div>

                <div className='integration-details'>
                    <p>{t(integrationData.description)}</p>
                </div>

                {IntegrationPageContent && (
                    // Render the specific integration page content
                    <IntegrationPageContent 
                        response={response} 
                        handleRefetch={refetch} 
                    />
                )}
                
                {effectiveStatus === 'error' && response?.last_error && (
                    <div className='integration-details'>
                        <div className='last-error'>
                            <div className='header'>{t('integrations.general.error_label')}</div>
                            {response?.last_error && (
                                <p>{t(`integrations.notifications.${slug}.${response.last_error}`)}</p>
                            )}
                        </div>
                    </div>
                )} 
            </div>

            {IntegrationPageActions && IntegrationPageForm && (
                // Render the specific integration page actions
                <IntegrationPageActions
                    response={response}
                    form={IntegrationPageForm}
                    integrationSlug={slug}
                    integrationId={id}
                    featureKey={featureKey}
                    handleRefetch={refetch}
                />
            )}

            <IntegrationNotificationsList
                integrationSlug={slug} 
                integrationId={id}
            />
        </>
    );
};

export default IntegrationPage;